import React, { useEffect, useState } from "react";
import { Download, WifiOff, X, Share, ChevronDown, CloudOff, Route, Zap } from "lucide-react";
import { BRAND } from "../lib/brand";

const WHY_INSTALL = [
    { icon: CloudOff, text: "Offline guides — open saved routes & photos with zero signal" },
    { icon: Route, text: "GPS routes ready at the trailhead, no loading" },
    { icon: Zap, text: "One-tap access from your home screen" },
];

const isStandalone = () =>
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)")?.matches ||
        window.navigator.standalone === true);

// iOS Safari (incl. iPadOS which reports as "MacIntel" + touch). Only Safari
// supports "Add to Home Screen", so exclude Chrome/Firefox/Edge on iOS.
const isIOS = () => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const iDevice =
        /iphone|ipad|ipod/i.test(ua) ||
        (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
    const isSafari = !/crios|fxios|edgios/i.test(ua);
    return iDevice && isSafari;
};

/**
 * PWA helpers:
 *  - "Install app" banner (Android/Chrome via beforeinstallprompt)
 *  - iOS "Add to Home Screen" hint (Safari has no install event)
 *  - Offline pill so users know they're seeing saved guides
 *
 * The prompt re-appears on every visit until the app is actually installed
 * (we never persist a "dismissed forever" flag) — dismissing only hides it for
 * the current session, and it returns on the next app open / reload.
 */
export default function PwaPrompts() {
    const [deferred, setDeferred] = useState(null);
    const [showInstall, setShowInstall] = useState(false);
    const [showIosTip, setShowIosTip] = useState(false);
    const [offline, setOffline] = useState(
        typeof navigator !== "undefined" && navigator.onLine === false
    );
    const [showWhy, setShowWhy] = useState(false);

    useEffect(() => {
        if (isStandalone()) return; // already installed → never prompt

        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
            setShowInstall(true);
        };
        const onInstalled = () => {
            setShowInstall(false);
            setShowIosTip(false);
            setDeferred(null);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("appinstalled", onInstalled);

        // iOS never fires beforeinstallprompt — show a gentle hint instead.
        let iosTimer;
        if (isIOS()) iosTimer = setTimeout(() => setShowIosTip(true), 2500);

        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
            if (iosTimer) clearTimeout(iosTimer);
        };
    }, []);

    useEffect(() => {
        const on = () => setOffline(false);
        const off = () => setOffline(true);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => {
            window.removeEventListener("online", on);
            window.removeEventListener("offline", off);
        };
    }, []);

    // Dismiss only for the current session (in-memory) — it returns next visit.
    const dismiss = () => {
        setShowInstall(false);
        setShowIosTip(false);
    };

    const install = async () => {
        if (!deferred) return;
        deferred.prompt();
        try { await deferred.userChoice; } catch (e) { /* ignore */ }
        setDeferred(null);
        setShowInstall(false);
    };

    return (
        <>
            {offline && (
                <div
                    data-testid="offline-pill"
                    className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-midnight-pine text-white text-xs font-bold shadow-lg"
                >
                    <WifiOff className="w-3.5 h-3.5" />
                    Offline — showing your saved guides
                </div>
            )}

            {(showInstall || showIosTip) && (
                <div
                    data-testid="pwa-install-banner"
                    className="fixed inset-x-3 z-[55] bottom-[calc(5rem+env(safe-area-inset-bottom))] lg:inset-x-auto lg:right-6 lg:bottom-6 lg:w-96"
                >
                    <div className="relative flex items-start gap-3 bg-snowcap border border-ridge-line rounded-xl p-4 shadow-2xl">
                        <button
                            data-testid="pwa-install-dismiss"
                            onClick={dismiss}
                            aria-label="Dismiss"
                            className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center text-slate-gray hover:bg-alpine-mist"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <img src={BRAND.logoUrl} alt="" className="w-11 h-11 rounded-lg shrink-0" />
                        <div className="min-w-0 pr-5">
                            <p className="font-display font-black text-sm text-midnight-pine leading-tight">
                                Take {BRAND.name} to the trail
                            </p>
                            {showInstall ? (
                                <>
                                    <p className="text-xs text-slate-gray mt-0.5">
                                        Install the app for one-tap access and offline guides &amp; routes when you lose signal.
                                    </p>
                                    <button
                                        data-testid="pwa-install-btn"
                                        onClick={install}
                                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ember-orange text-white text-sm font-bold hover:bg-ember-orange/90 transition-all"
                                    >
                                        <Download className="w-4 h-4" /> Install app
                                    </button>
                                </>
                            ) : (
                                <p className="text-xs text-slate-gray mt-1 flex items-center gap-1 flex-wrap">
                                    Tap <Share className="w-3.5 h-3.5 inline text-ember-orange" /> then
                                    <span className="font-bold text-midnight-pine">“Add to Home Screen”</span>
                                    for offline guides on the trail.
                                </p>
                            )}

                            <button
                                data-testid="pwa-why-toggle"
                                onClick={() => setShowWhy((v) => !v)}
                                aria-expanded={showWhy}
                                className="mt-2.5 inline-flex items-center gap-1 text-xs font-bold text-midnight-pine hover:text-ember-orange transition-colors"
                            >
                                Why install?
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showWhy ? "rotate-180" : ""}`} />
                            </button>
                            {showWhy && (
                                <ul data-testid="pwa-why-list" className="mt-2 space-y-1.5">
                                    {WHY_INSTALL.map(({ icon: Icon, text }) => (
                                        <li key={text} className="flex items-start gap-2 text-xs text-slate-gray">
                                            <Icon className="w-3.5 h-3.5 text-ember-orange mt-0.5 shrink-0" />
                                            <span>{text}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
